import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, Mail, Globe } from "lucide-react";
import { useLanguage } from "@/LanguageContext";
import { t } from "@/translations";
import { useSelection } from "@/SelectionContext";
import NoBlocksModal from "@/components/NoBlocksModal";
import NotificationBell from "@/components/NotificationBell";
import InstallAppButton from "@/components/InstallAppButton";

const VALLARTA_LOGO = "https://customer-assets.emergentagent.com/job_82ee4e38-abb0-4bd3-985f-83a63905409f/artifacts/3xn4ya4w_Vallarta%20Pixel.png";

export const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { lang, toggleLang } = useLanguage();
  const { selectedBlocks, getBuyUrl } = useSelection();
  const [showNoBlocks, setShowNoBlocks] = useState(false);

  const handleBuyClick = (e) => {
    e.preventDefault();
    if (selectedBlocks.length > 0) {
      navigate(getBuyUrl());
    } else {
      setShowNoBlocks(true);
    }
  };

  const navLinks = [
    { path: "/", label: t("nav.billboard", lang) },
    { path: "/about", label: t("nav.vallartaPixel", lang) },
    { path: "/faq", label: t("nav.faq", lang) },
    { path: "/blog", label: t("nav.blogPress", lang) },
    { path: "/my-blocks", label: t("nav.myBlocks", lang) },
  ];

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    // Capture referral code from URL params on any page
    const params = new URLSearchParams(location.search);
    const ref = params.get("ref");
    if (ref) {
      sessionStorage.setItem("referral_code", ref.toUpperCase());
      fetch(`${BACKEND_URL}/api/referral/track`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ referral_code: ref, page: location.pathname }),
      }).catch(() => {});
    }
  }, [location, BACKEND_URL]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileMenuOpen]);

  return (
    <>
    <header className="header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img 
              src={VALLARTA_LOGO} 
              alt="Vallarta Pixel" 
              className="h-16 w-16 object-contain"
              width="64"
              height="64"
              style={{ aspectRatio: "1/1" }}
            />
            <div className="hidden sm:block">
              <h1 className="font-heading font-bold text-lg text-[#0F172A] leading-tight">
                Vallarta Pixel
              </h1>
              <p className="font-pixel text-sm text-[#64748B] tracking-wider">
                The Golden Pixel Awards
              </p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link ${location.pathname === link.path ? "active" : ""}`}
              >
                {link.label}
              </Link>
            ))}
            <NotificationBell />
            <button
              onClick={toggleLang}
              className="flex items-center gap-1.5 px-3 py-1.5 border-2 border-[#E2E8F0] hover:border-[#14B8A6] transition-colors text-sm font-pixel"
              title={lang === "en" ? "Cambiar a Español" : "Switch to English"}
              data-testid="lang-toggle-btn"
            >
              <Globe size={16} className="text-[#14B8A6]" />
              <span className="text-[#0F172A]">{lang === "en" ? "ES" : "EN"}</span>
            </button>
            <button
              onClick={handleBuyClick}
              className="btn-primary"
              data-testid="header-buy-pixels-btn"
            >
              {selectedBlocks.length > 0
                ? `${t("nav.buyPixels", lang)} (${selectedBlocks.length})`
                : t("nav.buyPixels", lang)}
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            <Menu size={24} />
          </button>
        </div>
      </div>
    </header>

    {/* Mobile Menu — rendered OUTSIDE header to avoid z-index stacking issues */}
    {mobileMenuOpen && (
      <div
        className="mobile-menu-backdrop"
        onClick={() => setMobileMenuOpen(false)}
        data-testid="mobile-menu-backdrop"
      />
    )}
    <div
      className={`mobile-menu-drawer ${mobileMenuOpen ? "open" : ""}`}
      data-testid="mobile-menu-drawer"
    >
      <div className="flex justify-between items-center px-5 py-4 border-b-2 border-[#E2E8F0]">
        <img src={VALLARTA_LOGO} alt="Vallarta Pixel" className="h-12 w-12 object-contain" width="48" height="48" style={{ aspectRatio: "1/1" }} />
        <button
          onClick={() => setMobileMenuOpen(false)}
          className="p-2 hover:bg-gray-100 rounded"
          aria-label="Close menu"
          data-testid="mobile-menu-close"
        >
          <X size={24} />
        </button>
      </div>
      <nav className="flex flex-col px-5 py-4 gap-1">
        {navLinks.map((link) => (
          <Link
            key={link.path}
            to={link.path}
            onClick={() => setMobileMenuOpen(false)}
            className={`font-pixel text-sm px-3 py-3 border-l-4 transition-colors ${
              location.pathname === link.path
                ? "border-[#14B8A6] text-[#14B8A6] bg-[#14B8A6]/5"
                : "border-transparent text-[#0F172A] hover:border-[#0F172A]"
            }`}
            data-testid={`mobile-nav-${link.path.replace("/", "") || "home"}`}
          >
            {link.label}
          </Link>
        ))}
        {/* Buy Pixels — prominent CTA inside nav list */}
        <button
          onClick={(e) => { setMobileMenuOpen(false); handleBuyClick(e); }}
          className={`font-pixel text-sm px-3 py-3 border-l-4 transition-colors font-bold text-left w-full ${
            location.pathname === "/buy"
              ? "border-[#14B8A6] text-[#14B8A6] bg-[#14B8A6]/5"
              : "border-[#14B8A6] text-[#14B8A6] hover:bg-[#14B8A6]/5"
          }`}
          data-testid="mobile-nav-buy-pixels"
        >
          {t("nav.buyPixels", lang)}
        </button>
        {/* Language toggle — prominent, inside nav list */}
        <button
          onClick={() => { toggleLang(); setMobileMenuOpen(false); }}
          className="flex items-center gap-2 px-3 py-3 border-l-4 border-transparent text-[#0F172A] hover:border-[#14B8A6] transition-colors font-pixel text-sm w-full text-left"
          data-testid="mobile-lang-toggle"
        >
          <Globe size={16} className="text-[#14B8A6]" />
          {lang === "en" ? "Traducir a Español" : "Switch to English"}
        </button>
        <InstallAppButton variant="mobile-menu" />
      </nav>
      <div className="flex items-center gap-3 px-5 py-4 border-t border-[#E2E8F0] mt-auto">
        <NotificationBell />
        <button
          onClick={(e) => { setMobileMenuOpen(false); handleBuyClick(e); }}
          className="btn-primary flex-1 text-center"
          data-testid="mobile-buy-pixels-btn"
        >
          {selectedBlocks.length > 0
            ? `${t("nav.buyPixels", lang)} (${selectedBlocks.length})`
            : t("nav.buyPixels", lang)}
        </button>
      </div>
    </div>

    <NoBlocksModal isOpen={showNoBlocks} onClose={() => setShowNoBlocks(false)} />
    </>
  );
};

export const Footer = () => {
  const { lang } = useLanguage();

  return (
    <footer className="footer py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Brand */}
          <div>
            <Link to="/" data-testid="footer-logo-link">
              <img 
                src={VALLARTA_LOGO} 
                alt="Vallarta Pixel logo" 
                className="h-16 w-16 mb-4 hover:opacity-80 transition-opacity object-contain"
                width="64"
                height="64"
                loading="lazy"
                style={{ aspectRatio: "1/1" }}
              />
            </Link>
            <p className="text-sm text-gray-400">
              {t("footer.tagline", lang)}
            </p>
          </div>

          {/* Links */}
          <div>
            <h3 className="font-heading font-bold text-white mb-4">{t("footer.quickLinks", lang)}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Link to="/about" className="text-sm hover:text-[#F59E0B]">{t("footer.about", lang)}</Link>
              <Link to="/faq" className="text-sm hover:text-[#F59E0B]">{t("nav.faq", lang)}</Link>
              <Link to="/blog" className="text-sm hover:text-[#F59E0B]">{t("footer.blog", lang)}</Link>
              <Link to="/golden-tier" className="text-sm hover:text-[#F59E0B]">{t("footer.goldenTier", lang)}</Link>
              <Link to="/my-blocks" className="text-sm hover:text-[#F59E0B]">{t("nav.myBlocks", lang)}</Link>
              <Link to="/privacy" className="text-sm hover:text-[#F59E0B]">{t("footer.privacy", lang)}</Link>
              <Link to="/terms" className="text-sm hover:text-[#F59E0B]">{t("footer.terms", lang)}</Link>
              <Link to="/affiliate" className="text-sm hover:text-[#8B5CF6] text-[#8B5CF6]" data-testid="footer-affiliate-link">
                {lang === "en" ? "Become an Affiliate" : "Hazte Afiliado"}
              </Link>
              <InstallAppButton variant="footer" />
            </div>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-heading font-bold text-white mb-4">{t("footer.contact", lang)}</h3>
            <div className="flex items-center gap-5 mt-2">
              <a 
                href="mailto:ddarious@proton.me" 
                className="text-gray-400 hover:text-[#14B8A6] transition-colors"
                aria-label="Email"
                data-testid="footer-email-link"
              >
                <Mail size={22} />
              </a>
              <a 
                href="https://t.me/VallartaPixelOfficial" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-[#0088cc] transition-colors"
                aria-label="Telegram"
                data-testid="footer-telegram-link"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.913.489-1.302.481-.428-.008-1.252-.241-1.865-.44-.752-.244-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635.099-.002.321.023.465.141.121.1.154.234.17.332.015.098.036.322.02.496z"/>
                </svg>
              </a>
              <a
                href="https://facebook.com/VallartaPixelOfficial"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-[#1877F2] transition-colors"
                aria-label="Facebook"
                data-testid="footer-facebook-link"
              >
                <i className="fa-brands fa-facebook text-[22px]"></i>
              </a>
              <a
                href="https://instagram.com/VallartaPixelOfficial"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-[#E4405F] transition-colors"
                aria-label="Instagram"
                data-testid="footer-instagram-link"
              >
                <i className="fa-brands fa-instagram text-[22px]"></i>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-8 border-t border-gray-700">
          <p className="text-sm text-gray-400 text-center">
            © 2026 Vallarta Pixel | {t("layout.allRightsReserved", lang)} | {t("layout.poweredBy", lang)}{" "}
            <a 
              href="https://ddmtai.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#2DD4BF] hover:text-[#F59E0B] font-bold"
              style={{ textDecoration: "underline" }}
            >
              DARIO
            </a>
          </p>
          <p className="text-xs text-gray-400 text-center mt-2">
            {t("footer.disclaimer", lang)}
          </p>
          <p className="text-[11px] text-gray-500 text-center mt-3 tracking-wide" data-testid="footer-built-with">
            {t("layout.builtWith", lang)}{" "}
            <span aria-hidden="true" className="text-[#8B5CF6]">💜</span>
            <span className="sr-only">love</span>{" "}
            {t("layout.inPV", lang)}{" "}
            <span aria-hidden="true">·</span>{" "}
            <Link
              to="/"
              className="text-[#2DD4BF] hover:text-[#F59E0B] underline underline-offset-2"
              title="Vallarta Pixel — Puerto Vallarta digital billboard"
              data-testid="footer-built-with-link"
            >
              Vallarta Pixel
            </Link>
          </p>
        </div>
      </div>
    </footer>
  );
};

export const Layout = ({ children, showHero = false }) => {
  const location = useLocation();

  // Track page view on navigation
  useEffect(() => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    fetch(`${BACKEND_URL}/api/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ page: location.pathname, referrer: document.referrer }),
    }).catch(() => {});
  }, [location.pathname]);

  return (
    <div className="page-container">
      <Header />
      <main className="main-content">
        {children}
      </main>
      <Footer />
    </div>
  );
};

export default Layout;
